// ============================================================
// SettingsTab.jsx  —  Noble Enterprises (tape module)
//
// Two sections: Microns, and Expense categories.
//
// Microns feed the Purchase Order form. A micron in use is
// locked — its value is printed on rolls, cores, stock and
// invoices — so it cannot be edited, deactivated or deleted.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. Microns            — collapsed by default
//   C. ExpenseCategories  — collapsed by default
//   D. SettingsTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Check, Wallet, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children, hint }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
    {hint && (
      <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>
    )}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Toggle = ({ checked, onChange, label, disabled, title }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    style={{
      display: 'flex', alignItems: 'center', gap: '9px',
      background: 'none', border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      padding: 0, fontFamily: 'inherit',
    }}
  >
    <span style={{
      width: '38px', height: '22px', borderRadius: '11px',
      background: checked ? C.brand : C.border,
      position: 'relative', transition: 'background 0.15s', flexShrink: 0,
    }}>
      <span style={{
        position: 'absolute', top: '3px', left: checked ? '19px' : '3px',
        width: '16px', height: '16px', borderRadius: '50%',
        background: '#ffffff', transition: 'left 0.15s',
      }} />
    </span>
    <span style={{ fontSize: '14px', color: C.text }}>{label}</span>
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

// Header row shared by both collapsible cards
const CardHeader = ({ open, onToggle, title, icon, count, loading, blurb, action }) => (
  <div
    onClick={onToggle}
    style={{
      padding: '18px 22px',
      borderBottom: open ? `1px solid ${C.borderSoft}` : 'none',
      display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
      cursor: 'pointer', userSelect: 'none',
    }}
  >
    <ChevronRight
      size={18}
      style={{
        color: C.textMuted, flexShrink: 0,
        transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
        transition: 'transform 0.15s',
      }}
    />

    <div style={{ flex: 1, minWidth: '200px' }}>
      <h3 style={{
        margin: 0, fontSize: '16px', fontWeight: '700', color: C.text,
        display: 'flex', alignItems: 'center', gap: '9px',
      }}>
        {icon}
        {title}
        <span style={{
          fontSize: '12px', fontWeight: '600', color: C.textMuted,
          background: C.borderSoft, padding: '2px 9px', borderRadius: '11px',
        }}>
          {loading ? '…' : count}
        </span>
      </h3>
      {open && (
        <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>{blurb}</p>
      )}
    </div>

    {/* stopPropagation, or clicking the button would also collapse the card */}
    {open && action && <span onClick={e => e.stopPropagation()}>{action}</span>}
  </div>
);

// Edit/delete button that greys out when the row is in use
const LockableBtn = ({ locked, title, lockedTitle, color, onClick, children }) => (
  <button
    title={locked ? lockedTitle : title}
    disabled={locked}
    onClick={onClick}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: locked ? C.textMuted : color,
      cursor: locked ? 'not-allowed' : 'pointer',
      opacity: locked ? 0.45 : 1,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    {children}
  </button>
);


// ============================================================
// B. MICRONS
//
// Film thickness and its weight per square metre. Purchase
// orders pick from this list.
// ============================================================

const Microns = ({ notify }) => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen]       = useState(false);
  const [adding, setAdding]   = useState(false);
  const [draft, setDraft]     = useState({ micron: '', gsm: '' });
  const [editId, setEditId]   = useState(null);
  const [edit, setEdit]       = useState({ micron: '', gsm: '' });
  const [busy, setBusy]       = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tape/settings/microns`, { headers });
      setRows(res.data.data || []);
    } catch {
      notify('error', 'Could not load microns. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const invalid = (d) => {
    if (!(Number(d.micron) > 0)) return 'Enter the micron value';
    if (!(Number(d.gsm) > 0))    return 'Enter the weight per square metre';
    return null;
  };

  const add = async () => {
    const err = invalid(draft);
    if (err) return notify('error', err);
    setBusy(true);
    try {
      await axios.post(`${API}/tape/settings/microns`, draft, { headers });
      notify('success', 'Micron added');
      setDraft({ micron: '', gsm: '' });
      setAdding(false);
      load();
    } catch (e) {
      notify('error', e.response?.data?.message || 'Could not add');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (row) => {
    const err = invalid(edit);
    if (err) return notify('error', err);
    setBusy(true);
    try {
      await axios.put(
        `${API}/tape/settings/microns/${row.id}`,
        { ...edit, is_active: row.is_active },
        { headers }
      );
      notify('success', 'Micron updated');
      setEditId(null);
      load();
    } catch (e) {
      notify('error', e.response?.data?.message || 'Could not update');
    } finally {
      setBusy(false);
    }
  };

  // Sends the stored values back unchanged, which the server
  // allows even for a micron in use.
  const toggleActive = async (row) => {
    try {
      await axios.put(
        `${API}/tape/settings/microns/${row.id}`,
        { micron: row.micron, gsm: row.gsm, is_active: !row.is_active },
        { headers }
      );
      load();
    } catch (e) {
      notify('error', e.response?.data?.message || 'Could not update');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${Number(row.micron)} µ?`)) return;
    try {
      await axios.delete(`${API}/tape/settings/microns/${row.id}`, { headers });
      notify('success', 'Micron deleted');
      load();
    } catch (e) {
      notify('error', e.response?.data?.message || 'Could not delete');
    }
  };

  const smallInput = { ...inputStyle, padding: '6px 9px', maxWidth: '110px' };

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: '12px', overflow: 'hidden',
    }}>
      <CardHeader
        open={open}
        onToggle={() => setOpen(o => !o)}
        title="Microns"
        count={rows.length}
        loading={loading}
                blurb="Film thickness and weight per square metre. A micron in use is locked — it cannot be edited, deactivated or deleted."
        action={!adding && (
          <Btn onClick={() => setAdding(true)}><Plus size={16} /> Add micron</Btn>
        )}
      />

      {open && (
        <>
          {adding && (
            <div style={{
              padding: '16px 22px', background: C.rowAlt,
              borderBottom: `1px solid ${C.borderSoft}`,
              display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap',
            }}>
              <div style={{ width: '140px' }}>
                <Field label="Micron (µ)">
                  <TextInput
                    autoFocus type="number" step="0.1"
                    value={draft.micron}
                    onChange={e => setDraft({ ...draft, micron: e.target.value })}
                    placeholder="39"
                  />
                </Field>
              </div>
              <div style={{ width: '160px' }}>
                <Field label="Weight (g/m²)">
                  <TextInput
                    type="number" step="0.001"
                    value={draft.gsm}
                    onChange={e => setDraft({ ...draft, gsm: e.target.value })}
                    onKeyDown={e => e.key === 'Enter' && add()}
                    placeholder="37.7"
                  />
                </Field>
              </div>
              <Btn onClick={add} disabled={busy}>
                <Check size={16} /> {busy ? 'Saving…' : 'Save'}
              </Btn>
              <Btn variant="ghost" onClick={() => { setAdding(false); setDraft({ micron: '', gsm: '' }); }}>
                Cancel
              </Btn>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Micron</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>g/m²</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Used</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '30px' }}>
                      Loading…
                    </td>
                  </tr>
                )}

                {!loading && rows.map(row => {
                  const inUse     = Number(row.used_count) > 0;
                  const isEditing = editId === row.id;

                  return (
                    <tr key={row.id}>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>
                        {isEditing ? (
                          <input
                            autoFocus type="number" step="0.1" style={smallInput}
                            value={edit.micron}
                            onChange={e => setEdit({ ...edit, micron: e.target.value })}
                          />
                        ) : `${Number(row.micron)} µ`}
                      </td>

                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {isEditing ? (
                          <input
                            type="number" step="0.001"
                            style={{ ...smallInput, textAlign: 'right' }}
                            value={edit.gsm}
                            onChange={e => setEdit({ ...edit, gsm: e.target.value })}
                            onKeyDown={e => {
                              if (e.key === 'Enter')  saveEdit(row);
                              if (e.key === 'Escape') setEditId(null);
                            }}
                          />
                        ) : Number(row.gsm)}
                      </td>

                      <td style={{ ...tdStyle, textAlign: 'right', color: inUse ? C.text : C.textMuted }}>
                        {row.used_count}
                      </td>

                      <td style={tdStyle}>
                                                <Toggle
                          checked={row.is_active}
                          onChange={() => toggleActive(row)}
                          label={row.is_active ? 'Active' : 'Inactive'}
                          disabled={inUse && row.is_active}
                          title={inUse && row.is_active ? 'In use — cannot be deactivated' : undefined}
                        />
                      </td>

                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '7px' }}>
                          {isEditing ? (
                            <>
                              <IconBtn title="Save" onClick={() => saveEdit(row)}>
                                <Check size={15} />
                              </IconBtn>
                              <IconBtn title="Cancel" onClick={() => setEditId(null)}>
                                <X size={15} />
                              </IconBtn>
                            </>
                          ) : (
                            <>
                              <LockableBtn
                                locked={inUse}
                                title="Edit"
                                lockedTitle="In use — values are locked"
                                color={C.textSoft}
                                onClick={() => {
                                  setEditId(row.id);
                                  setEdit({ micron: Number(row.micron), gsm: Number(row.gsm) });
                                }}
                              >
                                <Pencil size={15} />
                              </LockableBtn>
                              <LockableBtn
                                locked={inUse}
                                title="Delete"
                                lockedTitle="In use — deactivate instead"
                                color={C.danger}
                                onClick={() => remove(row)}
                              >
                                <Trash2 size={15} />
                              </LockableBtn>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                      No microns yet. Click "Add micron" to create the first one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};


// ============================================================
// C. EXPENSE CATEGORIES
//
// Drives the dropdown on the Expenses screen. A category with
// entries behind it can only be deactivated, never deleted —
// removing it would orphan the expenses and quietly change
// every P&L that has already been read.
// ============================================================

const ExpenseCategories = ({ notify }) => {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(false);
  const [newName, setNewName]   = useState('');
  const [editId, setEditId]     = useState(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy]         = useState(false);
  const [open, setOpen]         = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tape/finance/categories?all=true`, { headers });
      setRows(res.data.data || []);
    } catch {
      notify('error', 'Could not load expense categories');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const name = newName.trim();
    if (!name) return notify('error', 'Category name is required');

    setBusy(true);
    try {
      await axios.post(`${API}/tape/finance/categories`, { name }, { headers });
      notify('success', 'Category added');
      setNewName('');
      setAdding(false);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not add');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (row) => {
    const name = editName.trim();
    if (!name) return notify('error', 'Category name cannot be blank');

    setBusy(true);
    try {
      await axios.put(`${API}/tape/finance/categories/${row.id}`, { name }, { headers });
      notify('success', 'Category updated');
      setEditId(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not update');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.put(
        `${API}/tape/finance/categories/${row.id}`,
        { is_active: !row.is_active },
        { headers }
      );
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not update');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/tape/finance/categories/${row.id}`, { headers });
      notify('success', 'Category deleted');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: '12px', overflow: 'hidden', marginTop: '20px',
    }}>
      <CardHeader
        open={open}
        onToggle={() => setOpen(o => !o)}
        title="Expense categories"
        icon={<Wallet size={17} color={C.brand} />}
        count={rows.length}
        loading={loading}
        blurb="Used on the Expenses screen and to group the P&L. A category in use can be deactivated but not deleted."
        action={!adding && (
          <Btn onClick={() => setAdding(true)}><Plus size={16} /> Add category</Btn>
        )}
      />

      {open && (
        <>
          {adding && (
            <div style={{
              padding: '16px 22px', background: C.rowAlt,
              borderBottom: `1px solid ${C.borderSoft}`,
              display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap',
            }}>
              <div style={{ flex: '1 1 240px', maxWidth: '340px' }}>
                <Field label="Category name">
                  <TextInput
                    autoFocus
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && add()}
                    placeholder="e.g. Machine Maintenance"
                  />
                </Field>
              </div>
              <Btn onClick={add} disabled={busy}>
                <Check size={16} /> {busy ? 'Saving…' : 'Save'}
              </Btn>
              <Btn variant="ghost" onClick={() => { setAdding(false); setNewName(''); }}>
                Cancel
              </Btn>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Category</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Entries</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '30px' }}>
                      Loading…
                    </td>
                  </tr>
                )}

                {!loading && rows.map(row => {
                  const inUse     = Number(row.used_count) > 0;
                  const isEditing = editId === row.id;

                  return (
                    <tr key={row.id}>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>
                        {isEditing ? (
                          <div style={{ maxWidth: '280px' }}>
                            <TextInput
                              autoFocus
                              value={editName}
                              onChange={e => setEditName(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter')  saveEdit(row);
                                if (e.key === 'Escape') setEditId(null);
                              }}
                            />
                          </div>
                        ) : row.name}
                      </td>

                      <td style={{ ...tdStyle, textAlign: 'right', color: inUse ? C.text : C.textMuted }}>
                        {row.used_count}
                      </td>

                      <td style={tdStyle}>
                        <Toggle
                          checked={row.is_active}
                          onChange={() => toggleActive(row)}
                          label={row.is_active ? 'Active' : 'Inactive'}
                        />
                      </td>

                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '7px' }}>
                          {isEditing ? (
                            <>
                              <IconBtn title="Save" onClick={() => saveEdit(row)}>
                                <Check size={15} />
                              </IconBtn>
                              <IconBtn title="Cancel" onClick={() => setEditId(null)}>
                                <X size={15} />
                              </IconBtn>
                            </>
                          ) : (
                            <>
                              <IconBtn
                                title="Rename"
                                onClick={() => { setEditId(row.id); setEditName(row.name); }}
                              >
                                <Pencil size={15} />
                              </IconBtn>
                              <LockableBtn
                                locked={inUse}
                                title="Delete"
                                lockedTitle="In use — deactivate instead"
                                color={C.danger}
                                onClick={() => remove(row)}
                              >
                                <Trash2 size={15} />
                              </LockableBtn>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                      No categories yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};


// ============================================================
// D. MAIN COMPONENT
// ============================================================

const SettingsTab = () => {
  const [banner, setBanner] = useState({ type: '', message: '' });

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      <Microns notify={notify} />
      <ExpenseCategories notify={notify} />
    </div>
  );
};

export default SettingsTab;